import React, { useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import { useDashboard } from '../../../contexts/DashboardContext';
import PanelExportActions from '../PanelExportActions';

export default function DashboardTable({ panelId, outputKey = 'table_data', config }) {
  const {
    getSlice, runDrillDownFlow, setSelectedRow, triggerInterviewFlowHandoff,
    exportPanelArtifact, config: appConfig,
  } = useDashboard();
  const drillDownFlowId = config?.drillDownFlowId || config?.drillDownFlow_id;
  const drillDownTargetPanelId = config?.drillDownTargetPanelId;
  const raw = getSlice(panelId, outputKey);

  const { columns, rows } = useMemo(() => {
    if (!raw || typeof raw !== 'object') {
      return { columns: [], rows: [] };
    }
    const cols = Array.isArray(raw.columns) ? raw.columns : [];
    const r = (Array.isArray(raw.rows) ? raw.rows : []).map((row, i) => ({
      ...row,
      id: row.id ?? row._id ?? i,
    }));
    const gridCols = cols.map((c) => ({
      field: c.field || c.id || String(c),
      headerName: c.headerName || c.label || c.field || c.id || String(c),
      width: c.width ?? 120,
      ...(c.flex ? {
        flex: c.flex,
        minWidth: c.minWidth ?? 120,
      } : {}),
    }));
    return { columns: gridCols, rows: r };
  }, [raw]);

  if (!columns.length && !rows.length) {
    return (
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 120,
      }}
      >
        <Typography color="text.secondary" sx={{ textAlign: 'center', maxWidth: 360 }}>
          {config?.emptyMessage || 'No table data. Use chat to run a flow.'}
        </Typography>
      </Box>
    );
  }

  const handleRowClick = (params) => {
    setSelectedRow?.(params.row);
    const handoff = triggerInterviewFlowHandoff?.(params.row);
    const handoffMeta = appConfig?.metadata?.interviewFlow?.handoff;
    if (handoff && handoffMeta?.companionAppId && !handoffMeta?.targetPhaseId) {
      window.open(`/studio/apps/${handoffMeta.companionAppId}`, '_blank');
    }
    if (drillDownFlowId && drillDownTargetPanelId && runDrillDownFlow) {
      runDrillDownFlow(drillDownFlowId, params.row, drillDownTargetPanelId);
    }
  };

  /** InterviewFlow + any Studio V2 / App Builder runtime that provides exportPanelArtifact */
  const hasExport = typeof exportPanelArtifact === 'function';
  const canExport = hasExport && (columns.length || rows.length);

  return (
    <Box sx={{ width: '100%', minHeight: 200, p: 1 }}>
      {hasExport && (
        <PanelExportActions
          panelId={panelId}
          panelLabel={config?.title || 'Table'}
          disabled={!canExport}
          onExportJson={() => exportPanelArtifact?.(panelId, 'json')}
          onExportPdf={() => exportPanelArtifact?.(panelId, 'pdf')}
        />
      )}
      <DataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[5, 10, 25]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        autoHeight
        sx={{ border: 'none', cursor: drillDownFlowId && drillDownTargetPanelId ? 'pointer' : 'default' }}
        onRowClick={handleRowClick}
      />
    </Box>
  );
}
